"use client";

import { Calendar, Clock, ArrowRight, User, Loader2, Globe, Pencil } from "lucide-react";
import { useRouter } from "nextjs-toploader/app";
import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { NewAppointmentDialog } from "@/app/(authenticated)/appointments/_modules/components/NewAppointmentDialog";
import { useGetAppointmentsWithFilters } from "@/app/(authenticated)/appointments/_modules/hooks/queries";
import type { Appointment } from "@/app/(authenticated)/appointments/_modules/types/appointments";
import type { GetAppointmentByIdResponseData } from "@/app/(authenticated)/appointments/_modules/types/get-appointment-by-id";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";

// State to timezone mapping (IANA timezone strings)
const stateTimezones: Record<string, { timezone: string; label: string }> = {
  AL: { timezone: "America/Chicago", label: "Central" },
  AK: { timezone: "America/Anchorage", label: "Alaska" },
  AZ: { timezone: "America/Phoenix", label: "Mountain" },
  AR: { timezone: "America/Chicago", label: "Central" },
  CA: { timezone: "America/Los_Angeles", label: "Pacific" },
  CO: { timezone: "America/Denver", label: "Mountain" },
  CT: { timezone: "America/New_York", label: "Eastern" },
  DE: { timezone: "America/New_York", label: "Eastern" },
  FL: { timezone: "America/New_York", label: "Eastern" },
  GA: { timezone: "America/New_York", label: "Eastern" },
  HI: { timezone: "Pacific/Honolulu", label: "Hawaii" },
  ID: { timezone: "America/Boise", label: "Mountain" },
  IL: { timezone: "America/Chicago", label: "Central" },
  IN: { timezone: "America/Indiana/Indianapolis", label: "Eastern" },
  IA: { timezone: "America/Chicago", label: "Central" },
  KS: { timezone: "America/Chicago", label: "Central" },
  KY: { timezone: "America/New_York", label: "Eastern" },
  LA: { timezone: "America/Chicago", label: "Central" },
  ME: { timezone: "America/New_York", label: "Eastern" },
  MD: { timezone: "America/New_York", label: "Eastern" },
  MA: { timezone: "America/New_York", label: "Eastern" },
  MI: { timezone: "America/Detroit", label: "Eastern" },
  MN: { timezone: "America/Chicago", label: "Central" },
  MS: { timezone: "America/Chicago", label: "Central" },
  MO: { timezone: "America/Chicago", label: "Central" },
  MT: { timezone: "America/Denver", label: "Mountain" },
  NE: { timezone: "America/Chicago", label: "Central" },
  NV: { timezone: "America/Los_Angeles", label: "Pacific" },
  NH: { timezone: "America/New_York", label: "Eastern" },
  NJ: { timezone: "America/New_York", label: "Eastern" },
  NM: { timezone: "America/Denver", label: "Mountain" },
  NY: { timezone: "America/New_York", label: "Eastern" },
  NC: { timezone: "America/New_York", label: "Eastern" },
  ND: { timezone: "America/Chicago", label: "Central" },
  OH: { timezone: "America/New_York", label: "Eastern" },
  OK: { timezone: "America/Chicago", label: "Central" },
  OR: { timezone: "America/Los_Angeles", label: "Pacific" },
  PA: { timezone: "America/New_York", label: "Eastern" },
  RI: { timezone: "America/New_York", label: "Eastern" },
  SC: { timezone: "America/New_York", label: "Eastern" },
  SD: { timezone: "America/Chicago", label: "Central" },
  TN: { timezone: "America/Chicago", label: "Central" },
  TX: { timezone: "America/Chicago", label: "Central" },
  UT: { timezone: "America/Denver", label: "Mountain" },
  VT: { timezone: "America/New_York", label: "Eastern" },
  VA: { timezone: "America/New_York", label: "Eastern" },
  WA: { timezone: "America/Los_Angeles", label: "Pacific" },
  WV: { timezone: "America/New_York", label: "Eastern" },
  WI: { timezone: "America/Chicago", label: "Central" },
  WY: { timezone: "America/Denver", label: "Mountain" },
  DC: { timezone: "America/New_York", label: "Eastern" },
  PR: { timezone: "America/Puerto_Rico", label: "Atlantic" },
};

// Format time with timezone info
const formatTimeWithTimezone = (time: string, date: string | undefined, leadState: string | undefined) => {
  if (!time) return { agentTime: null, clientTime: null, sameTimezone: true, hasLeadState: false };

  // Normalize time to HH:MM format
  const timeParts = time.split(":");
  const hours = parseInt(timeParts[0] || "0");
  const minutes = parseInt(timeParts[1] || "0");

  const agentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Create a date object with the time
  const tempDate = new Date();
  tempDate.setHours(hours, minutes, 0, 0);

  const agentTimeStr = tempDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const agentTzAbbr = new Intl.DateTimeFormat('en-US', {
    timeZone: agentTimezone,
    timeZoneName: 'short'
  }).formatToParts(tempDate).find(p => p.type === 'timeZoneName')?.value || 'Local';

  if (!leadState || !stateTimezones[leadState]) {
    return { agentTime: agentTimeStr, agentTz: agentTzAbbr, clientTime: null, clientTz: null, sameTimezone: true, hasLeadState: false };
  }

  const tzInfo = stateTimezones[leadState];

  // Calculate client's time
  let clientTimeStr = agentTimeStr;
  if (date) {
    try {
      // Normalize date to YYYY-MM-DD format
      const dateStr = typeof date === 'string' ? date : String(date);
      let normalizedDate = dateStr;

      // If date looks like ISO format, extract just the date part
      if (dateStr.includes('T')) {
        normalizedDate = dateStr.split('T')[0];
      }

      // Create date using local date parts to avoid timezone issues
      const [year, month, day] = normalizedDate.split('-').map(Number);
      const fullDate = new Date(year, month - 1, day, hours, minutes, 0);

      if (!isNaN(fullDate.getTime())) {
        clientTimeStr = fullDate.toLocaleTimeString("en-US", {
          timeZone: tzInfo.timezone,
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }
    } catch {
      // Fallback to agent time
    }
  }

  // Check if same timezone
  const sameTimezone = tzInfo.timezone === agentTimezone;

  return {
    agentTime: agentTimeStr,
    agentTz: agentTzAbbr,
    clientTime: clientTimeStr,
    clientTz: tzInfo.label,
    sameTimezone,
    hasLeadState: true,
  };
};

// Helper to extract lead ID from appointment
const getLeadIdFromAppointment = (appointment: GetAppointmentByIdResponseData): string | undefined => {
  if (appointment?.leadId) {
    return appointment.leadId;
  }
  const leadAssoc = appointment?.associations?.find(a => a.entityType === "Lead");
  return leadAssoc?.entityId;
};

// Helper to extract lead name from appointment associations
const getLeadNameFromAppointment = (appointment: GetAppointmentByIdResponseData): string | undefined => {
  const leadAssoc = appointment?.associations?.find(a => a.entityType === "Lead");
  return leadAssoc?.entityName || undefined;
};

export const AppointmentsPreview = function () {
  const navigate = useRouter();
  const queryClient = useQueryClient();
  const [currentAgencyId, setCurrentAgencyId] = React.useState<string | null>(
    AgencyTokenManager.getCurrentAgencyId()
  );

  const {
    data: appointmentsData,
    isLoading,
    error,
    refetch,
  } = useGetAppointmentsWithFilters("", "all", 1, 50);

  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editingAppointment, setEditingAppointment] = React.useState<Appointment | undefined>(undefined);

  // Invalidate and refetch appointments when agency changes
  React.useEffect(() => {
    const intervalId = setInterval(() => {
      const newAgencyId = AgencyTokenManager.getCurrentAgencyId();
      if (newAgencyId !== currentAgencyId) {
        setCurrentAgencyId(newAgencyId);
        queryClient.invalidateQueries({ queryKey: ['appointments'] });
      }
    }, 500); // Check every 500ms for agency changes

    return () => clearInterval(intervalId);
  }, [currentAgencyId, queryClient]);

  const parseStartDateTime = (appointment: GetAppointmentByIdResponseData): Date | null => {
    try {
      if (appointment.startTime) {
        const d = new Date(typeof appointment.startTime === 'string' || appointment.startTime instanceof Date ? appointment.startTime : String(appointment.startTime));
        return isNaN(d.getTime()) ? null : d;
      }
      if (appointment.date) {
        // Parse YYYY-MM-DD as local date to avoid UTC shift
        const dateStr = typeof appointment.date === 'string' ? appointment.date : String(appointment.date);
        const [year, month, day] = dateStr.split('-').map(Number);
        const [hh, mm] = (appointment.time || "00:00").split(":");
        const d = new Date(year, month - 1, day, parseInt(hh || "0", 10), parseInt(mm || "0", 10), 0, 0);
        if (isNaN(d.getTime())) return null;
        return d;
      }
      return null;
    } catch {
      return null;
    }
  };

  const getFallbackTimeFromStart = (appointment: GetAppointmentByIdResponseData): string => {
    const d = parseStartDateTime(appointment);
    if (!d) return "";
    const hh = d.getHours();
    const mm = d.getMinutes().toString().padStart(2, "0");
    return `${hh.toString().padStart(2, "0")}:${mm}`;
  };

  const getFallbackDateStrFromStart = (appointment: GetAppointmentByIdResponseData): string => {
    const d = parseStartDateTime(appointment);
    return d ? d.toISOString() : "";
  };

  const toEditable = (src: GetAppointmentByIdResponseData): Appointment => ({
    id: src.id,
    userId: src.userId,
    title: src.title,
    time: (typeof src.time === 'string' && src.time) ? src.time : getFallbackTimeFromStart(src),
    date: (typeof src.date === 'string' && src.date) ? src.date : getFallbackDateStrFromStart(src),
    notes: src.notes,
    description: src.description,
    startTime: (src.startTime as unknown) as Date | string | undefined,
    endTime: (src.endTime as unknown) as Date | string | undefined,
    meetingLink: src.meetingLink,
    leadId: src.leadId,
    agencyId: src.agencyId,
    priorityStatus: (src.priorityStatus as unknown) as Appointment['priorityStatus'],
    createdAt: src.createdAt,
    updatedAt: src.updatedAt,
    deletedAt: null,
    status: src.status,
    type: src.type || undefined,
    location: src.location || undefined,
    duration: (src as unknown as { duration?: number }).duration,
    associations: (src.associations as unknown) as Appointment['associations'],
  });

  const upcomingAppointments = React.useMemo(() => {
    if (!appointmentsData?.items) return [];

    const now = new Date();

    return appointmentsData.items
      .slice()
      .filter((appointment: GetAppointmentByIdResponseData) => {
        const start = parseStartDateTime(appointment);
        if (!start) return false;
        // Only show appointments that are in the future (including time)
        return start > now;
      })
      .sort((a, b) => {
        const da = parseStartDateTime(a)?.getTime() ?? Infinity;
        const db = parseStartDateTime(b)?.getTime() ?? Infinity;
        return da - db;
      })
      .slice(0, 3);
  }, [appointmentsData]);

  // Extract all unique lead IDs from upcoming appointments
  const leadIds = React.useMemo(() => {
    const ids = upcomingAppointments
      .map(getLeadIdFromAppointment)
      .filter((id): id is string => !!id);
    return [...new Set(ids)];
  }, [upcomingAppointments]);

  // Batch fetch all lead data in a single query
  const { data: leadsData } = useQuery({
    queryKey: ['leads-for-timezone-batch', leadIds],
    queryFn: async () => {
      if (leadIds.length === 0) return {};

      // Fetch all leads in parallel
      const results = await Promise.all(
        leadIds.map(async (leadId) => {
          try {
            const response = await api.get({
              url: getApiUrl(endpoint.leads.getLeadById(leadId)),
              auth: true,
            });
            const lead = (response as any)?.data?.data || (response as any)?.data || response;
            const name = lead?.name || (lead?.firstName && lead?.lastName ? `${lead.firstName} ${lead.lastName}` : lead?.firstName || undefined);
            return { leadId, state: lead?.state, name };
          } catch {
            return { leadId, state: undefined, name: undefined };
          }
        })
      );

      // Convert to a map for quick lookup
      const leadStateMap: Record<string, { state?: string; name?: string }> = {};
      results.forEach(({ leadId, state, name }) => {
        leadStateMap[leadId] = { state, name };
      });
      return leadStateMap;
    },
    enabled: leadIds.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Helper to get lead state from the batch data
  const getLeadState = (appointment: GetAppointmentByIdResponseData): string | undefined => {
    const leadId = getLeadIdFromAppointment(appointment);
    if (!leadId || !leadsData) return undefined;
    return leadsData[leadId]?.state;
  };

  // Helper to get lead name (from batch data or association)
  const getLeadName = (appointment: GetAppointmentByIdResponseData): string | undefined => {
    const leadId = getLeadIdFromAppointment(appointment);
    if (leadId && leadsData?.[leadId]?.name) return leadsData[leadId].name;
    return getLeadNameFromAppointment(appointment);
  };

  const formatTime = (time: string) => {
    try {
      if (!time) return "";
      const [hours, minutes] = time.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return time;
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      if (!dateStr) return "";

      // Parse YYYY-MM-DD as local date to avoid UTC timezone shift
      let date: Date;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateStr);
      }

      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const dateToCheck = date.toDateString();
      const todayStr = today.toDateString();
      const tomorrowStr = tomorrow.toDateString();

      if (dateToCheck === todayStr) return "Today";
      if (dateToCheck === tomorrowStr) return "Tomorrow";

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusStyling = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "scheduled":
      case "confirmed":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "cancelled":
      case "no_show":
      case "no-show":
        return "bg-red-100 text-red-800 border-red-200";
      case "completed":
        return "bg-primary/20 text-primary border-primary/20";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Statuses arrive snake_cased from the API ("no_show") — humanize before
  // display: underscores/hyphens → spaces, sentence case ("No show").
  const formatStatus = (status?: string) => {
    if (!status) return "Pending";
    const clean = status.replace(/[_-]+/g, " ").trim().toLowerCase();
    return clean.charAt(0).toUpperCase() + clean.slice(1);
  };

  const openAppointmentDialog = (item: GetAppointmentByIdResponseData) => {
    setEditingAppointment(toEditable(item));
    setIsDialogOpen(true);
  };

  // Inside the native shell every screen lives under /m.
  const shellPrefix = typeof window !== "undefined" && window.location.pathname.startsWith("/m") ? "/m" : "";
  const goToAppointments = () => navigate.push(`${shellPrefix}/appointments`);

  if (isLoading) {
    return (
      <Card className="h-full shadow-sm border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg font-semibold text-gray-900">
            Upcoming Appointments
          </CardTitle>
          <Calendar className="h-5 w-5 text-primary" />
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading appointments...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full shadow-sm border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg font-semibold text-gray-900">
            Upcoming Appointments
          </CardTitle>
          <Calendar className="h-5 w-5 text-primary" />
        </CardHeader>
        <CardContent className="py-6 space-y-4">
          <div className="text-center">
            <Calendar className="h-10 w-10 mx-auto mb-3 text-red-300" />
            <p className="text-sm font-medium text-gray-900 mb-1">
              Unable to load appointments
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Please check your connection or try again
            </p>
            <Button
              className="text-primary border-primary/20 hover:bg-primary/10"
              onClick={() => refetch()}
              size="sm"
              variant="outline"
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full shadow-sm border-gray-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg font-semibold text-gray-900">
          Upcoming Appointments
        </CardTitle>
        <Calendar className="h-5 w-5 text-primary" />
      </CardHeader>
      <CardContent className="space-y-4">
        {upcomingAppointments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm font-medium">No upcoming appointments</p>
            <p className="text-xs text-gray-400 mt-1">Your schedule is clear</p>
          </div>
        ) : (
          <React.Fragment>
            <div className="space-y-3">
              {upcomingAppointments.map((appointment: GetAppointmentByIdResponseData) => {
                // Prefer startTime over legacy time/date fields (legacy fields can be stale)
                const timeValue = (appointment.startTime ? getFallbackTimeFromStart(appointment) : appointment.time || getFallbackTimeFromStart(appointment)) as string;
                const dateValue = (appointment.startTime ? getFallbackDateStrFromStart(appointment) : appointment.date || getFallbackDateStrFromStart(appointment)) as string;
                const leadState = getLeadState(appointment);
                const leadName = getLeadName(appointment);
                const leadId = getLeadIdFromAppointment(appointment);
                const tzInfo = formatTimeWithTimezone(timeValue, dateValue, leadState);

                return (
                  <div
                    key={appointment.id}
                    className="flex items-start gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-all duration-200 cursor-pointer"
                    onClick={() => {
                      if (leadId) {
                        navigate.push(`${shellPrefix}/leads/${leadId}`);
                      } else {
                        openAppointmentDialog(appointment);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (leadId) {
                          navigate.push(`${shellPrefix}/leads/${leadId}`);
                        } else {
                          openAppointmentDialog(appointment);
                        }
                      }
                    }}
                  >
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-3 h-12 bg-primary rounded-full" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 leading-tight">
                          {appointment.title}
                        </p>
                        {!!appointment.status && (
                          <Badge
                            className={`${getStatusStyling(appointment.status)} text-xs border font-medium`}
                          >
                            {formatStatus(appointment.status)}
                          </Badge>
                        )}
                      </div>
                      {leadName && (
                        <div className="flex items-center gap-1.5">
                          <User className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-700 font-medium">{leadName}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(dateValue)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>{formatTime(timeValue)} ({tzInfo.agentTz})</span>
                        </div>
                      </div>
                      {tzInfo.hasLeadState && !tzInfo.sameTimezone && (
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Globe className="h-3 w-3" />
                          <span>Client: {tzInfo.clientTime} ({tzInfo.clientTz})</span>
                        </div>
                      )}
                      {(appointment.notes || appointment.description) && (
                        <p className="text-xs text-gray-500 line-clamp-2">
                          {appointment.notes || appointment.description}
                        </p>
                      )}
                    </div>
                    {/* Edit button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-shrink-0 h-7 w-7 p-0 text-gray-400 hover:text-gray-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        openAppointmentDialog(appointment);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>

            <div className="pt-3 border-t border-gray-200">
              <Button
                className="w-full justify-between text-primary hover:text-primary/80 hover:bg-primary/10 h-10 font-medium"
                onClick={goToAppointments}
                variant="ghost"
              >
                View all appointments
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </React.Fragment>
        )}
      </CardContent>
      <NewAppointmentDialog
        editingAppointment={editingAppointment}
        onOpenChange={() => setIsDialogOpen(false)}
        onSave={() => {}}
        open={isDialogOpen}
      />
    </Card>
  );
};